import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { UserListItemDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Upload01Icon } from "@/components/Icons/Upload01Icon";
import { useModalState } from "@/components/Modal/Modal";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { ReinviteUserModal } from "@/components/ReinviteUserModal/ReinviteUserModal";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { UserTable, UserTableSensitiveColumn } from "@/components/UserTable/UserTable";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { Stack } from "@/design-system/Stack";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { canEditOtherUserNotificationSettings } from "@/helpers/permissions";
import { parseQueryParamToList, parseQueryParamToString } from "@/helpers/query-params";
import type { UserStatus } from "@/helpers/user-statuses";
import { getUserStatusLabelByType, USER_STATUSES } from "@/helpers/user-statuses";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSlug } from "@/hooks/useSlug";
import { canAccessBulkUploadUsers, canAssignRoles, canInviteUser } from "@/modules/users/permissions";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import type { UsersRouteParams } from "../../routes";
import { DeleteAdminModal } from "../details/components/DeleteAdminModal";
import { DeleteResidentModal } from "../details/components/DeleteResidentModal";
import { UserExportModal } from "./components/UserExportModal";
import { type UserFilterValues, UserOverviewFilterDrawer } from "./components/UserOverviewFilterDrawer";

type UserOverviewTab = "all" | "residents" | "admins";
type UsersParams = Omit<ApiQueryParams<"getUsersV1">, "Limit" | "Offset">;

export function UserListPage(): React.ReactNode {
  const [isFilterDrawerOpened, filterDrawerHandler] = useBool(false);
  const [isExportModalOpened, exportModalOpenHandlers] = useBool(false);
  const [userToBeReinvited, setUserToBeReinvited] = useState<UserListItemDto | undefined>(undefined);
  const deleteUserModal = useModalState<UserListItemDto>();
  const [queryParams, setQueryParams] = useQueryParams<UsersRouteParams["list"]>();
  const [search, setSearch] = useState(() => queryParams.search ?? "");
  const sortBy = (queryParams.sortBy ?? "invitedAt") as UsersParams["SortBy"];
  const sortDescending = queryParams.sortDescending !== "false";

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = usePermission();

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);

  const companyQueries = useCompanyQueries();
  const { data: companiesData, isLoading: isLoadingCompaniesData } = useQuery({
    ...companyQueries.list(),
    enabled: sessionUser.project.type === "companyBased",
  });
  const currFilter: UserFilterValues = useMemo(
    () => ({
      companyId: parseQueryParamToString(queryParams.companyId),
      statuses: parseQueryParamToList(queryParams.statuses, (currStatus) => USER_STATUSES.includes(currStatus as any)),
    }),
    [queryParams],
  );
  const tabs: TabItem<UserOverviewTab>[] = useMemo(
    () => [
      {
        label: t("common.label.all"),
        value: "all",
        "data-testid": "users-all-tab-btn",
      },
      {
        label: t("common.entity.resident", { count: 2 }),
        value: "residents",
        "data-testid": "users-residents-tab-btn",
      },
      {
        label: t("common.entity.admin", { count: 2 }),
        value: "admins",
        "data-testid": "users-admins-tab-btn",
      },
    ],
    [t],
  );

  const requestedTab = tabs.find((tab) => tab.value === queryParams.tab);
  const requestedTabValue = requestedTab?.value;
  const activeTab: UserOverviewTab | undefined = sessionUser.isAdmin ? requestedTabValue : "all";
  const activeTabParam = activeTab === "residents" || activeTab === "admins" ? activeTab : undefined;

  useEffect(() => {
    const defaultQueryParams: Partial<UsersRouteParams["list"]> = {};
    if (!activeTab) {
      defaultQueryParams.tab = tabs[0].value;
    }

    if (currFilter.statuses === undefined || currFilter.statuses.length === 0) {
      defaultQueryParams.statuses = (["invited", "onboarded"] satisfies UserStatus[]).join(",");
    }

    setQueryParams({
      ...queryParams,
      ...defaultQueryParams,
    });
  }, [queryParams, activeTab, setQueryParams, currFilter.statuses, tabs]);

  const userQueries = useUserQueries();
  const userListQuery = useMemo(
    () => ({
      Search: debouncedQuery,
      CompanyId: currFilter.companyId,
      Tab: activeTabParam,
      Statuses: currFilter.statuses,
      SortBy: sortBy,
      SortDescending: sortDescending,
    }),
    [activeTabParam, currFilter.companyId, currFilter.statuses, debouncedQuery, sortBy, sortDescending],
  );
  const {
    data: usersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isFetching: isFetchingUsers,
    isFetchingNextPage: isFetchingMoreUsers,
    error: errorUsers,
  } = useInfiniteQuery({
    ...userQueries.listInfinite(userListQuery),
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  const refLoader = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreUsers && hasMoreUsers) {
        void fetchMoreUsers();
      }
    }, [isFetchingMoreUsers, hasMoreUsers, fetchMoreUsers]),
  });

  const handleUpdateFilter = (values: UserFilterValues) => {
    setQueryParams({
      ...queryParams,
      companyId: values.companyId,
      statuses: values.statuses && values.statuses.length > 0 ? values.statuses.join(",") : undefined,
    });
  };

  const handleSelectTab = (tab: UserOverviewTab) => {
    setQueryParams({
      ...queryParams,
      tab: tab,
    });
  };

  const handleDeleteUserModalOpenChange = (isOpened: boolean) => {
    if (!isOpened) {
      deleteUserModal.requestClose();
    }
  };

  const getTableActions = (user: UserListItemDto) => {
    return [
      {
        labels: {
          default: t("common.action.edit"),
        },
        value: "edit",
        icon: <Edit05Icon />,
        callbackFn: () => void navigate(routes.users.edit({ slug, id: user.id })),
        isHidden: !user.canBeEdited,
      },
      {
        labels: {
          default: t("component.user-table.data.actions.edit-notifications"),
        },
        value: "edit-notifications",
        icon: <Settings01Icon />,
        callbackFn: () =>
          void navigate(routes.adminNotifications.otherUserSettings({ slug, userId: user.id }), {
            state: {
              fromPage: routes.users.list({ slug }),
            },
          }),
        isHidden: !hasPermission(canEditOtherUserNotificationSettings) || !user.isAdmin || Boolean(user.deletedAt),
      },
      {
        labels: {
          default: t("component.user-table.data.actions.resend-invitation"),
        },
        value: "resend-invitation",
        icon: <Mail01Icon />,
        callbackFn: () => setUserToBeReinvited(user),
        isHidden:
          !(user.canBeReinvited || sessionUser.isSuperAdmin) ||
          Boolean(user.registeredAt) ||
          (user.isAdmin && !hasPermission((x) => x.userManagement.canEditAdminInfo)) ||
          (!user.isAdmin && !hasPermission((x) => x.userManagement.canEditResidentInfo)),
      },
      {
        labels: {
          default: t("common.action.delete"),
        },
        value: "delete",
        icon: <Trash01Icon />,
        callbackFn: () => {
          deleteUserModal.open(user);
        },
        isHidden: !sessionUser.isAdmin || !user.canBeDeleted,
      },
    ];
  };

  const users = useMemo(() => {
    // To override BE behaviour of returning all users when no status filter is applied, we return an empty list instead
    if (!currFilter.statuses || currFilter.statuses.length === 0) {
      return [];
    }

    return usersData?.pages.flatMap((page) => page.items) || [];
  }, [currFilter.statuses, usersData]);
  const amountTotalUsers = useMemo(() => {
    if (!currFilter.statuses || currFilter.statuses.length === 0) {
      return 0;
    }

    return usersData?.pages[0].total || 0;
  }, [currFilter.statuses, usersData?.pages]);
  const companies = companiesData?.items || [];
  const amountActiveFilters = Object.entries(currFilter).filter(([_, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    } else {
      return Boolean(value);
    }
  }).length;

  const error = errorUsers;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingCompaniesData;
  if (isLoading || !activeTab) {
    return <FullSizeLoader withPadding />;
  }

  // It's needed when it's a company based project
  // due to an extra filter field
  const isFilterDrawerNeeded = sessionUser.project.type === "companyBased";
  const userStatusOptions = USER_STATUSES.map((status) => ({
    id: status,
    name: getUserStatusLabelByType(t, status),
  })).filter((status) => status.id !== "deleted" || sessionUser.isSuperAdmin);
  const userListContent = (
    <Stack gap="sm">
      <span className="text-caption">
        {activeTab === "all" && t("page.user-overview.total-users", { count: amountTotalUsers })}
        {activeTab === "residents" && t("page.user-overview.total-residents", { count: amountTotalUsers })}
        {activeTab === "admins" && t("page.user-overview.total-admins", { count: amountTotalUsers })}
      </span>
      {(isFetchingUsers || users.length > 0) && (
        <UserTable
          data={users}
          isLoading={isFetchingUsers && !isFetchingMoreUsers}
          sortBy={{
            key: sortBy,
            isDescending: sortDescending,
          }}
          onSort={({ key, isDescending }) => {
            setQueryParams({
              ...queryParams,
              sortBy: key,
              sortDescending: String(isDescending),
            });
          }}
          renderActions={(userRow) => getTableActions(userRow as UserListItemDto)}
          sensitiveColumns={[
            ...(sessionUser.isAdmin ? [UserTableSensitiveColumn.PLATFORM_DETAILS] : []),
            ...(sessionUser.isSuperAdmin || hasPermission(canAssignRoles) ? [UserTableSensitiveColumn.ROLE] : []),
          ]}
        />
      )}
      {hasMoreUsers && <CollectionLoadingState ref={refLoader} />}
    </Stack>
  );
  const userToBeDeleted = deleteUserModal.data;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.user-overview.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.user-overview.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{
                scope: "project",
                entity: "users",
                page: "list",
                filters: {
                  tab: activeTab,
                  search: debouncedQuery,
                  statuses: currFilter.statuses,
                  companyId: currFilter.companyId,
                },
              }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.users.suggestion.1"),
                t("component.ask-nobi-chat-drawer.users.suggestion.2"),
              ]}
            />
            {hasPermission(canAccessBulkUploadUsers) && (
              <Button variant="soft" icon={<Upload01Icon />} type="link" href={routes.users.bulkUpload({ slug })}>
                {t("page.user-overview.bulk-upload")}
              </Button>
            )}
            <Button variant="soft" icon={<Download01Icon />} onClick={exportModalOpenHandlers.setTrue}>
              {t("common.action.export")}
            </Button>
            {hasPermission(canInviteUser) && (
              <Button type="link" data-testid="create-user-btn" href={routes.users.new({ slug })}>
                {t("page.user-overview.new-user")}
              </Button>
            )}
          </>
        }
        header={
          <Inline gap="sm" align="center">
            <SearchInput
              defaultValue={search}
              placeholder={t("page.user-overview.filters.search")}
              onChange={(e) => setSearch(e.target.value)}
            />
            {!isFilterDrawerNeeded && (
              <CheckboxMultiSelect
                renderMoreSelected={(count) => t("common.label.x-more", { count })}
                items={userStatusOptions}
                selected={userStatusOptions.filter((s) => currFilter.statuses?.includes(s.id))}
                onChange={(currStatus) =>
                  handleUpdateFilter({ ...currFilter, statuses: currStatus.map(({ id }) => id) })
                }
                placeholder={t("page.user-overview.filter-drawer.statuses.placeholder")}
                searchableFieldSelector={(x) => [x.name]}
                renderOption={(x) => {
                  let variant: "success" | "danger" | "warning" = "warning";
                  if (x.id === "onboarded") {
                    variant = "success";
                  } else if (x.id === "deleted") {
                    variant = "danger";
                  }

                  return <Badge variant={variant}>{x.name}</Badge>;
                }}
                hideClearButton
                keySelector={(x) => x.id}
              />
            )}
            {isFilterDrawerNeeded && (
              <Button
                data-testid="filter-btn"
                variant="soft"
                onClick={filterDrawerHandler.toggle}
                icon={<FilterFunnel01Icon />}
              >
                <Inline as="span" gap="sm" align="center" wrap={false}>
                  {t("common.action.filter")}
                  {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
                </Inline>
              </Button>
            )}
          </Inline>
        }
      >
        {sessionUser.isAdmin ? (
          <Tabs.Root activeTab={activeTab} onSelectTab={handleSelectTab} tabs={tabs}>
            <Tabs.Content value={activeTab}>{userListContent}</Tabs.Content>
          </Tabs.Root>
        ) : (
          userListContent
        )}
      </DocumentPage>

      {isFilterDrawerNeeded && (
        <UserOverviewFilterDrawer
          isOpened={isFilterDrawerOpened}
          onOpenChange={filterDrawerHandler.set}
          filter={currFilter}
          onUpdate={handleUpdateFilter}
          filterOptions={{
            companies,
          }}
        />
      )}
      <UserExportModal
        isOpened={isExportModalOpened}
        onOpenChange={exportModalOpenHandlers.set}
        query={userListQuery}
      />
      <ReinviteUserModal
        userId={userToBeReinvited?.id || ""}
        isOpened={Boolean(userToBeReinvited)}
        onOpenChange={(state) => {
          if (!state) {
            setUserToBeReinvited(undefined);
          }
        }}
      />
      {userToBeDeleted && userToBeDeleted.isAdmin && (
        <DeleteAdminModal
          user={userToBeDeleted}
          isOpened={deleteUserModal.isOpen}
          onOpenChange={handleDeleteUserModalOpenChange}
          onExitComplete={deleteUserModal.afterClose}
        />
      )}
      {userToBeDeleted && !userToBeDeleted.isAdmin && (
        <DeleteResidentModal
          user={userToBeDeleted}
          isOpened={deleteUserModal.isOpen}
          onOpenChange={handleDeleteUserModalOpenChange}
          onExitComplete={deleteUserModal.afterClose}
        />
      )}
    </>
  );
}
